import { JSX, useEffect } from "react";

import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import CssBaseline from "@mui/material/CssBaseline";
import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import { EnvProvider } from "./context/EnvContext";
import { SessionIdProvider } from "./context/SessionIdContext";
import { useAutoResize } from "./hooks/useAutoResize";
import { MinimalApp } from "./renderer/MinimalApp";
import { theme } from "./theme/theme";
import { getParsedUrlParams } from "./tools/helper";

const cacheNoPrefixer = createCache({
  key: "noprefixer",
  ...(process.env.NODE_ENV === "development" && { stylisPlugins: [] }),
});

export const queryClient = new QueryClient();

function App(): JSX.Element {
  useAutoResize();

  useEffect(() => {
    const root = document.getElementById("root");
    if (!root) return;
    const setVar = (): void =>
      document.documentElement.style.setProperty(
        "--app-root-height",
        `${root.clientHeight}px`,
      );
    setVar();
    const ro = new ResizeObserver(setVar);
    ro.observe(root);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const urlParams = getParsedUrlParams();
    Object.entries(urlParams).forEach(([key, value]) => {
      if (value) {
        queryClient.setQueryData(["urlParams", key], value);
      }
    });
  }, []);

  return (
    <CacheProvider value={cacheNoPrefixer}>
      <EnvProvider>
        <SessionIdProvider>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <QueryClientProvider client={queryClient}>
              <MinimalApp />
            </QueryClientProvider>
          </ThemeProvider>
        </SessionIdProvider>
      </EnvProvider>
    </CacheProvider>
  );
}

export default App;
